import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { LogoMark } from "@/components/Logo";
import { CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function PaymentCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const reference = params.get("reference") || params.get("trxref");
  const [state, setState] = useState("verifying");

  useEffect(() => {
    if (!reference) {
      setState("failed");
      return;
    }
    api
      .get(`/subscription/verify/${reference}`)
      .then(async ({ data }) => {
        if (data.status === "success") {
          // Activation is server-side; refresh the session if one exists.
          try { await refreshUser(); } catch (e) { /* session may be lost */ }
          setState("success");
        } else {
          setState("failed");
        }
      })
      .catch(() => setState("failed"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference]);

  const goHome = () => (window.location.href = user ? "/" : "/auth");

  return (
    <div className="debii-shell min-h-screen flex flex-col items-center justify-center px-6 text-center">
      {state === "verifying" && (
        <>
          <div className="animate-pop"><LogoMark size={64} /></div>
          <p className="mt-6 text-muted-foreground">Verifying your payment…</p>
        </>
      )}
      {state === "success" && (
        <>
          <CheckCircle2 className="h-16 w-16 text-emerald-600 animate-pop" />
          <h1 className="mt-4 font-display text-2xl font-bold">Welcome to Debii Plus 🎉</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Your subscription is active.{!user && " Please sign in to continue."}
          </p>
          <Button data-testid="payment-success-continue" onClick={goHome} className="mt-6 w-full h-12 rounded-xl">
            {user ? "Go to Home" : "Sign in"}
          </Button>
        </>
      )}
      {state === "failed" && (
        <>
          <XCircle className="h-16 w-16 text-red-500 animate-pop" />
          <h1 className="mt-4 font-display text-2xl font-bold">Payment not confirmed yet</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            If you were charged, don't worry — sign in and open Subscription and we'll confirm it automatically. You won't be charged again.
          </p>
          <Button data-testid="payment-failed-back" onClick={() => (window.location.href = user ? "/upgrade" : "/auth")} className="mt-6 w-full h-12 rounded-xl">
            {user ? "Back to upgrade" : "Sign in"}
          </Button>
        </>
      )}
    </div>
  );
}
